/**
 * Email Performance - Volume and engagement metrics with drill-down
 *
 * Route: /admin/reporting/email
 */

import { useState, useMemo, useCallback, useEffect } from 'react'
import clsx from 'clsx'
import { format, subMonths } from 'date-fns'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  LabelList,
  Legend,
} from 'recharts'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Select, type SelectOption } from '@/common/components/select'
import { DatePicker } from '@/common/components/date-picker'
import { useAuth } from '@/common/hooks/useAuth'
import {
  useEmailVolume,
  useEmailEngagement,
  useEmailVolumeBreakdown,
  useEmailVolumeRecords,
} from '@/features/reporting/hooks/useReportingData'
import { fetchEmailVolumeRecords } from '@/features/reporting/api/reportingApi'
import { Tooltip as InfoTooltip } from '@/common/components/tooltip'
import { ExportMenu } from '@/features/reporting/components/ExportMenu'
import { ChartModal, ExpandChartButton } from '@/features/reporting/components/ChartModal'
import { series, chrome, semantic, table, tooltipStyle, tickProps } from '@/features/reporting/chartPalette'
import * as styles from './index.css'

export const Route = createFileRoute({
  component: EmailPerformancePage,
})

type Granularity = 'month' | 'week' | 'day'
type DateRangePreset = '1y' | '6mo' | '3mo' | '1mo' | 'custom'
type SelectedChart = 'volume' | 'engagement' | 'counts'

function formatCount(value: number): string {
  return value.toLocaleString('en-US')
}

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`
}

function formatPeriodLabel(period: string): string {
  const weekMatch = period.match(/^(\d{4})-W(\d{2})$/)
  if (weekMatch) {
    const year = Number(weekMatch[1])
    const week = Number(weekMatch[2])
    const jan4 = new Date(year, 0, 4)
    const dayOfWeek = jan4.getDay() || 7
    const monday = new Date(jan4)
    monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7)
    return format(monday, 'MMM d')
  }
  const monthMatch = period.match(/^(\d{4})-(\d{2})$/)
  if (monthMatch) {
    const d = new Date(Number(monthMatch[1]), Number(monthMatch[2]) - 1, 1)
    return format(d, "MMM ''yy")
  }
  const dayMatch = period.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dayMatch) {
    const d = new Date(Number(dayMatch[1]), Number(dayMatch[2]) - 1, Number(dayMatch[3]))
    return format(d, 'MMM d')
  }
  return period
}

function calcDelta(current: number, prior: number | undefined): number | null {
  if (prior === undefined || prior === 0) return null
  return ((current - prior) / prior) * 100
}

function deltaColor(delta: number | null): string {
  if (delta === null) return semantic.neutral
  if (delta > 0) return semantic.positive
  if (delta < 0) return semantic.negative
  return semantic.neutral
}

function deltaLabel(delta: number | null): string {
  if (delta === null) return 'N/A'
  const sign = delta > 0 ? '+' : ''
  return `${sign}${delta.toFixed(1)}%`
}

function deltaIndicator(delta: number | null): string {
  if (delta === null) return ''
  if (delta > 0) return '\u25B2 '
  if (delta < 0) return '\u25BC '
  return ''
}

/** Parse a yyyy-MM-dd string as a local-time Date (avoids UTC midnight timezone shift) */
function parseDateAsLocal(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d)
}

const USER_TYPE_OPTIONS: SelectOption[] = [
  { value: 'investor', label: 'Investor' },
  { value: 'agent', label: 'Agent' },
]

function EmailPerformancePage() {
  const { token } = useAuth()
  const [granularity, setGranularity] = useState<Granularity>('month')
  const [selectedChart, setSelectedChart] = useState<SelectedChart>('volume')
  const [drillDownPeriod, setDrillInPeriod] = useState<string | null>(null)
  const [drillDownPage, setDrillInPage] = useState(1)
  const [dateRange, setDateRange] = useState<DateRangePreset>('1y')
  const [customStart, setCustomStart] = useState<Date | null>(null)
  const [customEnd, setCustomEnd] = useState<Date | null>(null)
  const [userType, setUserType] = useState<string | undefined>(undefined)
  const [chartExpanded, setChartExpanded] = useState(false)

  const { startDateStr, endDateStr } = useMemo(() => {
    const now = new Date()
    if (dateRange === 'custom') {
      return {
        startDateStr: format(customStart ?? subMonths(now, 12), 'yyyy-MM-dd'),
        endDateStr: format(customEnd ?? now, 'yyyy-MM-dd'),
      }
    }
    let start: Date
    switch (dateRange) {
      case '1y': start = subMonths(now, 12); break
      case '6mo': start = subMonths(now, 6); break
      case '3mo': start = subMonths(now, 3); break
      case '1mo': start = subMonths(now, 1); break
    }
    return { startDateStr: format(start, 'yyyy-MM-dd'), endDateStr: format(now, 'yyyy-MM-dd') }
  }, [dateRange, customStart, customEnd])

  useEffect(() => {
    setDrillInPeriod(null)
  }, [granularity, dateRange, selectedChart, userType])

  useEffect(() => {
    setDrillInPage(1)
  }, [drillDownPeriod])

  const { data: emailVolumeRes } = useEmailVolume(granularity, startDateStr, endDateStr, userType)
  const { data: emailEngagementRes } = useEmailEngagement(granularity, startDateStr, endDateStr, userType)
  const { data: breakdownRes } = useEmailVolumeBreakdown(drillDownPeriod, granularity, userType, startDateStr, endDateStr)
  const { data: recordsRes, isFetching: isRecordsFetching } = useEmailVolumeRecords(drillDownPeriod, granularity, userType, startDateStr, endDateStr, drillDownPage)

  const handleChartClick = useCallback(
    (nextState: { activeLabel?: string | number | undefined }) => {
      if (nextState.activeLabel != null) {
        setDrillInPeriod(String(nextState.activeLabel))
      }
    },
    []
  )

  const handleCloseDrillIn = useCallback(() => {
    setDrillInPeriod(null)
  }, [])

  const handleDateRangeChange = useCallback((preset: DateRangePreset) => {
    if (preset === 'custom' && dateRange !== 'custom') {
      const now = new Date()
      const starts: Record<Exclude<DateRangePreset, 'custom'>, Date> = {
        '1y': subMonths(now, 12), '6mo': subMonths(now, 6), '3mo': subMonths(now, 3),
        '1mo': subMonths(now, 1),
      }
      setCustomStart(starts[dateRange])
      setCustomEnd(now)
    }
    setDateRange(preset)
  }, [dateRange])

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.controls}>
            <div className={styles.controlSelect}>
              <Select label="User Type" options={USER_TYPE_OPTIONS} value={userType ?? ''} onValueChange={(val) => setUserType(val || undefined)} placeholder="All" optional showOptional={false} clearLabel="All" size="sm" />
            </div>
            <div className={styles.granularityToggle}>
              <button className={clsx(styles.granularityButton, granularity === 'month' && styles.granularityButtonActive)} onClick={() => setGranularity('month')}>Monthly</button>
              <button className={clsx(styles.granularityButton, granularity === 'week' && styles.granularityButtonActive)} onClick={() => setGranularity('week')}>Weekly</button>
              <button className={clsx(styles.granularityButton, granularity === 'day' && styles.granularityButtonActive)} onClick={() => setGranularity('day')}>Daily</button>
            </div>
            <div className={styles.controlDateGroup}>
              <div className={styles.granularityToggle}>
                {(['1y', '6mo', '3mo', '1mo', 'custom'] as const).map((preset) => (
                  <button key={preset} className={clsx(styles.granularityButton, dateRange === preset && styles.granularityButtonActive)} onClick={() => handleDateRangeChange(preset)}>
                    {preset === 'custom' ? 'Custom' : preset}
                  </button>
                ))}
              </div>
              <div className={styles.customDateRange}>
                <div className={styles.customDatePicker}>
                  <DatePicker label="Start" value={parseDateAsLocal(startDateStr)} onChange={setCustomStart} maxDate={parseDateAsLocal(endDateStr)} size="sm" disabled={dateRange !== 'custom'} />
                </div>
                <Text variant="bodySm" color="muted">{'\u2013'}</Text>
                <div className={styles.customDatePicker}>
                  <DatePicker label="End" value={parseDateAsLocal(endDateStr)} onChange={setCustomEnd} minDate={parseDateAsLocal(startDateStr)} maxDate={new Date()} size="sm" disabled={dateRange !== 'custom'} />
                </div>
              </div>
            </div>
          </div>

        {/* Metric Cards */}
        <InfoTooltip.Provider>
        <div className={styles.metricsGrid}>
          {/* Volume card */}
          <div
            className={clsx(styles.metricCard, selectedChart === 'volume' && styles.metricCardActive)}
            onClick={() => setSelectedChart('volume')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedChart('volume') } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Text variant="bodySm" color="muted">Total Sent</Text>
              <InfoTooltip content="The total number of emails sent to users in the selected period, across all email types (hourly match, daily digest, weekly digest, transactional). Source: COM platform email log." side="top" delayDuration={200}>
                <span style={{ display: 'inline-flex', alignItems: 'center', color: '#9ca3af', cursor: 'help', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm.75 7.75v-4h-1.5v4h1.5zM8 5.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z"/>
                  </svg>
                </span>
              </InfoTooltip>
            </div>
            <Text as="h4" variant="h4" weight="bold" style={{ margin: '4px 0' }}>
              {formatCount(emailVolumeRes?.data.at(-1)?.total_sent ?? 0)}
            </Text>
            <Text variant="bodyXs" style={{ color: deltaColor(calcDelta(emailVolumeRes?.data.at(-1)?.total_sent ?? 0, emailVolumeRes?.data.at(-2)?.total_sent)) }}>
              {deltaIndicator(calcDelta(emailVolumeRes?.data.at(-1)?.total_sent ?? 0, emailVolumeRes?.data.at(-2)?.total_sent))}
              {deltaLabel(calcDelta(emailVolumeRes?.data.at(-1)?.total_sent ?? 0, emailVolumeRes?.data.at(-2)?.total_sent))} vs prior
            </Text>
          </div>
          {/* Engagement Rates (merged) */}
          <div
            className={clsx(styles.metricCard, selectedChart === 'engagement' && styles.metricCardActive)}
            onClick={() => setSelectedChart('engagement')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedChart('engagement') } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
              <Text variant="bodySm" color="muted">Engagement Rates</Text>
              <InfoTooltip content="Open Rate: the percentage of sent emails that were opened. Click Rate: the percentage of sent emails where at least one link was clicked. Source: Mailgun webhook events." side="top" delayDuration={200}>
                <span style={{ display: 'inline-flex', alignItems: 'center', color: '#9ca3af', cursor: 'help', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm.75 7.75v-4h-1.5v4h1.5zM8 5.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z"/>
                  </svg>
                </span>
              </InfoTooltip>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
              <Text variant="bodyXs" color="muted">Open Rate</Text>
              <Stack direction="row" gap="8" align="baseline">
                <Text variant="body" weight="bold">{formatPct(emailEngagementRes?.data.at(-1)?.open_rate ?? 0)}</Text>
                <Text variant="bodyXs" style={{ color: deltaColor(calcDelta(emailEngagementRes?.data.at(-1)?.open_rate ?? 0, emailEngagementRes?.data.at(-2)?.open_rate)) }}>
                  {deltaIndicator(calcDelta(emailEngagementRes?.data.at(-1)?.open_rate ?? 0, emailEngagementRes?.data.at(-2)?.open_rate))}
                  {deltaLabel(calcDelta(emailEngagementRes?.data.at(-1)?.open_rate ?? 0, emailEngagementRes?.data.at(-2)?.open_rate))}
                </Text>
              </Stack>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Text variant="bodyXs" color="muted">Click Rate</Text>
              <Stack direction="row" gap="8" align="baseline">
                <Text variant="body" weight="bold">{formatPct(emailEngagementRes?.data.at(-1)?.click_rate ?? 0)}</Text>
                <Text variant="bodyXs" style={{ color: deltaColor(calcDelta(emailEngagementRes?.data.at(-1)?.click_rate ?? 0, emailEngagementRes?.data.at(-2)?.click_rate)) }}>
                  {deltaIndicator(calcDelta(emailEngagementRes?.data.at(-1)?.click_rate ?? 0, emailEngagementRes?.data.at(-2)?.click_rate))}
                  {deltaLabel(calcDelta(emailEngagementRes?.data.at(-1)?.click_rate ?? 0, emailEngagementRes?.data.at(-2)?.click_rate))}
                </Text>
              </Stack>
            </div>
          </div>
          {/* Opened & Clicked (merged) */}
          <div
            className={clsx(styles.metricCard, selectedChart === 'counts' && styles.metricCardActive)}
            onClick={() => setSelectedChart('counts')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedChart('counts') } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
              <Text variant="bodySm" color="muted">Opened & Clicked</Text>
              <InfoTooltip content="Raw counts of emails that were opened and emails where a link was clicked. Use this to see absolute engagement volume rather than rates. Source: Mailgun webhook events." side="top" delayDuration={200}>
                <span style={{ display: 'inline-flex', alignItems: 'center', color: '#9ca3af', cursor: 'help', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm.75 7.75v-4h-1.5v4h1.5zM8 5.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z"/>
                  </svg>
                </span>
              </InfoTooltip>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
              <Text variant="bodyXs" color="muted">Opened</Text>
              <Stack direction="row" gap="8" align="baseline">
                <Text variant="body" weight="bold">{formatCount(emailEngagementRes?.data.at(-1)?.opened ?? 0)}</Text>
                <Text variant="bodyXs" style={{ color: deltaColor(calcDelta(emailEngagementRes?.data.at(-1)?.opened ?? 0, emailEngagementRes?.data.at(-2)?.opened)) }}>
                  {deltaIndicator(calcDelta(emailEngagementRes?.data.at(-1)?.opened ?? 0, emailEngagementRes?.data.at(-2)?.opened))}
                  {deltaLabel(calcDelta(emailEngagementRes?.data.at(-1)?.opened ?? 0, emailEngagementRes?.data.at(-2)?.opened))}
                </Text>
              </Stack>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Text variant="bodyXs" color="muted">Clicked</Text>
              <Stack direction="row" gap="8" align="baseline">
                <Text variant="body" weight="bold">{formatCount(emailEngagementRes?.data.at(-1)?.clicked ?? 0)}</Text>
                <Text variant="bodyXs" style={{ color: deltaColor(calcDelta(emailEngagementRes?.data.at(-1)?.clicked ?? 0, emailEngagementRes?.data.at(-2)?.clicked)) }}>
                  {deltaIndicator(calcDelta(emailEngagementRes?.data.at(-1)?.clicked ?? 0, emailEngagementRes?.data.at(-2)?.clicked))}
                  {deltaLabel(calcDelta(emailEngagementRes?.data.at(-1)?.clicked ?? 0, emailEngagementRes?.data.at(-2)?.clicked))}
                </Text>
              </Stack>
            </div>
          </div>
        </div>
        </InfoTooltip.Provider>

        {/* Chart - switches based on selected metric */}
        {selectedChart === 'volume' && (
          <div className={styles.chartContainer}>
            <div className={styles.chartHeader}>
              <Text variant="bodySm" color="muted">
                Email Volume by Type
              </Text>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <ExpandChartButton onClick={() => setChartExpanded(true)} />
                <ExportMenu options={[
                  { label: 'Volume Data (CSV)', data: (emailVolumeRes?.data ?? []) as unknown as Record<string, unknown>[], filename: 'email-volume.csv' },
                ]} />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={emailVolumeRes?.data ?? []} onClick={handleChartClick}>
                <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} />
                <XAxis dataKey="period" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} tickFormatter={formatPeriodLabel} />
                <YAxis tick={tickProps} tickLine={false} axisLine={false} width={60} />
                <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} labelFormatter={(label) => formatPeriodLabel(String(label))} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="hourly_match" name="Hourly Match" stackId="email" fill={series.primary} cursor="pointer" isAnimationActive={false}>
                  <LabelList dataKey="hourly_match" position="center" style={{ fontSize: '10px', fill: '#fff' }} formatter={(v: unknown) => typeof v === 'number' && v > 0 ? v.toLocaleString() : ''} />
                </Bar>
                <Bar dataKey="daily_digest" name="Daily Digest" stackId="email" fill={series.teal} cursor="pointer" isAnimationActive={false}>
                  <LabelList dataKey="daily_digest" position="center" style={{ fontSize: '10px', fill: '#fff' }} formatter={(v: unknown) => typeof v === 'number' && v > 0 ? v.toLocaleString() : ''} />
                </Bar>
                <Bar dataKey="weekly_digest" name="Weekly Digest" stackId="email" fill={series.accent1} cursor="pointer" isAnimationActive={false}>
                  <LabelList dataKey="weekly_digest" position="center" style={{ fontSize: '10px', fill: '#fff' }} formatter={(v: unknown) => typeof v === 'number' && v > 0 ? v.toLocaleString() : ''} />
                </Bar>
                <Bar dataKey="transactional" name="Transactional" stackId="email" fill={series.neutral} radius={[4, 4, 0, 0]} cursor="pointer" isAnimationActive={false}>
                  <LabelList dataKey="transactional" position="center" style={{ fontSize: '10px', fill: '#fff' }} formatter={(v: unknown) => typeof v === 'number' && v > 0 ? v.toLocaleString() : ''} />
                  <LabelList dataKey="total_sent" position="top" style={{ fontSize: '10px', fill: table.headerText }} formatter={(v: unknown) => typeof v === 'number' ? v.toLocaleString() : ''} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {selectedChart === 'engagement' && (
          <div className={styles.chartContainer}>
            <div className={styles.chartHeader}>
              <Text variant="bodySm" color="muted">
                Engagement Rates Over Time
              </Text>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <ExpandChartButton onClick={() => setChartExpanded(true)} />
                <ExportMenu options={[
                  { label: 'Engagement Data (CSV)', data: (emailEngagementRes?.data ?? []) as unknown as Record<string, unknown>[], filename: 'email-engagement.csv' },
                ]} />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={emailEngagementRes?.data ?? []} onClick={handleChartClick}>
                <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} />
                <XAxis dataKey="period" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} tickFormatter={formatPeriodLabel} />
                <YAxis tick={tickProps} tickLine={false} axisLine={false} width={60} tickFormatter={(v: number) => `${v}%`} />
                <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} labelFormatter={(label) => formatPeriodLabel(String(label))} formatter={(value: number | undefined) => value != null ? `${value.toFixed(1)}%` : '-'} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="open_rate" name="Open Rate" stroke={series.primary} strokeWidth={2} dot={{ r: 3, cursor: 'pointer' }} isAnimationActive={false}>
                  <LabelList dataKey="open_rate" position="top" style={{ fontSize: '13px', fontWeight: 600, fill: series.primary }} formatter={(v: unknown) => typeof v === 'number' && v > 0 ? `${v.toFixed(1)}%` : ''} />
                </Line>
                <Line type="monotone" dataKey="click_rate" name="Click Rate" stroke={series.accent1} strokeWidth={2} dot={{ r: 3, cursor: 'pointer' }} isAnimationActive={false}>
                  <LabelList dataKey="click_rate" position="bottom" style={{ fontSize: '13px', fontWeight: 600, fill: series.accent1 }} formatter={(v: unknown) => typeof v === 'number' && v > 0 ? `${v.toFixed(1)}%` : ''} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {selectedChart === 'counts' && (
          <div className={styles.chartContainer}>
            <div className={styles.chartHeader}>
              <Text variant="bodySm" color="muted">
                Opened & Clicked Over Time
              </Text>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <ExpandChartButton onClick={() => setChartExpanded(true)} />
                <ExportMenu options={[
                  { label: 'Engagement Data (CSV)', data: (emailEngagementRes?.data ?? []) as unknown as Record<string, unknown>[], filename: 'email-engagement-counts.csv' },
                ]} />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={emailEngagementRes?.data ?? []} onClick={handleChartClick}>
                <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} />
                <XAxis dataKey="period" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} tickFormatter={formatPeriodLabel} />
                <YAxis tick={tickProps} tickLine={false} axisLine={false} width={60} />
                <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} labelFormatter={(label) => formatPeriodLabel(String(label))} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="opened" name="Opened" fill={series.primary} radius={[4, 4, 0, 0]} cursor="pointer" isAnimationActive={false}>
                  <LabelList dataKey="opened" position="top" style={{ fontSize: '10px', fill: series.primary }} formatter={(v: unknown) => typeof v === 'number' ? v.toLocaleString() : ''} />
                </Bar>
                <Bar dataKey="clicked" name="Clicked" fill={series.accent1} radius={[4, 4, 0, 0]} cursor="pointer" isAnimationActive={false}>
                  <LabelList dataKey="clicked" position="top" style={{ fontSize: '10px', fill: series.accent1 }} formatter={(v: unknown) => typeof v === 'number' ? v.toLocaleString() : ''} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <ChartModal
          open={chartExpanded}
          onClose={() => setChartExpanded(false)}
          title={selectedChart === 'volume' ? 'Email Volume by Type' : selectedChart === 'engagement' ? 'Engagement Rates Over Time' : 'Opened & Clicked Over Time'}
        >
          {selectedChart === 'volume' && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={emailVolumeRes?.data ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} />
                <XAxis dataKey="period" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} tickFormatter={formatPeriodLabel} />
                <YAxis tick={tickProps} tickLine={false} axisLine={false} width={60} />
                <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} labelFormatter={(label) => formatPeriodLabel(String(label))} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="hourly_match" name="Hourly Match" stackId="email" fill={series.primary} isAnimationActive={false} />
                <Bar dataKey="daily_digest" name="Daily Digest" stackId="email" fill={series.teal} isAnimationActive={false} />
                <Bar dataKey="weekly_digest" name="Weekly Digest" stackId="email" fill={series.accent1} isAnimationActive={false} />
                <Bar dataKey="transactional" name="Transactional" stackId="email" fill={series.neutral} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  <LabelList dataKey="total_sent" position="top" style={{ fontSize: '10px', fill: table.headerText }} formatter={(v: unknown) => typeof v === 'number' ? v.toLocaleString() : ''} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          {selectedChart === 'engagement' && (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={emailEngagementRes?.data ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} />
                <XAxis dataKey="period" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} tickFormatter={formatPeriodLabel} />
                <YAxis tick={tickProps} tickLine={false} axisLine={false} width={60} tickFormatter={(v: number) => `${v}%`} />
                <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} labelFormatter={(label) => formatPeriodLabel(String(label))} formatter={(value: number | undefined) => value != null ? `${value.toFixed(1)}%` : '-'} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="open_rate" name="Open Rate" stroke={series.primary} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                <Line type="monotone" dataKey="click_rate" name="Click Rate" stroke={series.accent1} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
          {selectedChart === 'counts' && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={emailEngagementRes?.data ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} />
                <XAxis dataKey="period" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} tickFormatter={formatPeriodLabel} />
                <YAxis tick={tickProps} tickLine={false} axisLine={false} width={60} />
                <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} labelFormatter={(label) => formatPeriodLabel(String(label))} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="opened" name="Opened" fill={series.primary} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  <LabelList dataKey="opened" position="top" style={{ fontSize: '10px', fill: series.primary }} formatter={(v: unknown) => typeof v === 'number' ? v.toLocaleString() : ''} />
                </Bar>
                <Bar dataKey="clicked" name="Clicked" fill={series.accent1} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  <LabelList dataKey="clicked" position="top" style={{ fontSize: '10px', fill: series.accent1 }} formatter={(v: unknown) => typeof v === 'number' ? v.toLocaleString() : ''} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartModal>

        {/* Drill-Down Panel */}
        {drillDownPeriod && (
          <div className={styles.drillDownPanel}>
            <div className={styles.drillDownHeader}>
              <Text variant="body" weight="bold">
                Email Breakdown: {formatPeriodLabel(drillDownPeriod)}
              </Text>
              <Button variant="ghost" size="sm" onClick={handleCloseDrillIn}>
                <Icon name="close" size={18} />
              </Button>
            </div>

            {/* Breakdown Chart */}
            <div style={{ position: 'relative', minHeight: '280px', marginBottom: '24px' }}>
              <div className={styles.chartHeader}>
                <Text variant="bodySm" color="muted">
                  Emails by Type
                </Text>
                <ExportMenu options={[
                  { label: 'Breakdown (CSV)', data: (breakdownRes?.data ?? []) as unknown as Record<string, unknown>[], filename: `email-breakdown-${drillDownPeriod}.csv` },
                ]} />
              </div>
              {!breakdownRes && (
                <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {breakdownRes?.data && breakdownRes.data.length > 0 && (
                <ResponsiveContainer width="100%" height={Math.max(200, breakdownRes.data.length * 36)}>
                  <BarChart data={breakdownRes.data} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} horizontal={false} />
                    <XAxis type="number" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} />
                    <YAxis type="category" dataKey="label" tick={tickProps} tickLine={false} axisLine={false} width={120} />
                    <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} />
                    <Bar dataKey="value" fill={series.primary} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                      <LabelList dataKey="value" position="right" style={{ fontSize: '11px', fill: table.headerText }} formatter={(v: unknown) => typeof v === 'number' ? v.toLocaleString() : ''} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Records Table */}
            <div style={{ position: 'relative', minHeight: '400px' }}>
              {(!recordsRes || isRecordsFetching) && (
                <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {recordsRes?.data && recordsRes.data.length > 0 && (
                <>
                <div className={styles.chartHeader}>
                  <Text variant="bodySm" weight="bold">Email Records</Text>
                  <ExportMenu options={[
                    { label: 'Export Page', data: (recordsRes?.data ?? []) as unknown as Record<string, unknown>[], filename: `email-records-${drillDownPeriod}-page${drillDownPage}.csv` },
                    ...(recordsRes?.pagination && recordsRes.pagination.total_pages > 1 ? [{
                      label: 'Export All Records',
                      description: `${recordsRes.pagination.total_count.toLocaleString()} records — may take a moment`,
                      fetchData: async () => {
                        if (!token || !drillDownPeriod) return []
                        const allData: Record<string, unknown>[] = []
                        const totalPages = recordsRes.pagination!.total_pages
                        for (let p = 1; p <= totalPages; p++) {
                          const res = await fetchEmailVolumeRecords(token, { period: drillDownPeriod, granularity, user_type: userType, start_date: startDateStr, end_date: endDateStr, page: p, page_size: 100 })
                          allData.push(...(res.data as unknown as Record<string, unknown>[]))
                        }
                        return allData
                      },
                      filename: `email-records-${drillDownPeriod}-all.csv`,
                    }] : []),
                  ]} />
                </div>
                <table className={styles.recordsTable}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Recipient</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Type</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Subject</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Sent</th>
                      <th style={{ textAlign: 'center', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Opened</th>
                      <th style={{ textAlign: 'center', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Clicked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recordsRes.data.map((record) => (
                      <tr key={record.id}>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}` }}>{record.recipient_email}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText, textTransform: 'capitalize' }}>
                          {record.email_type.replace(/_/g, ' ')}
                        </td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {record.subject ?? '-'}
                        </td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>
                          {record.sent_at
                            ? new Date(record.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                            : '-'}
                        </td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'center' }}>{record.opened ? 'Yes' : '-'}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'center' }}>{record.clicked ? 'Yes' : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {recordsRes.pagination && (
                  <div className={styles.paginationControls}>
                    <Text variant="bodyXs" color="muted">
                      Page {recordsRes.pagination.page} of {recordsRes.pagination.total_pages}
                      {' '}({recordsRes.pagination.total_count} records)
                    </Text>
                    <Stack direction="row" gap="8">
                      <Button variant="outline" size="sm" disabled={isRecordsFetching || recordsRes.pagination.page <= 1} onClick={() => setDrillInPage((p) => Math.max(1, p - 1))}>
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" disabled={isRecordsFetching || !recordsRes.pagination.has_more} onClick={() => setDrillInPage((p) => p + 1)}>
                        Next
                      </Button>
                    </Stack>
                  </div>
                )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
